import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  hcGetCrqsToCreate,
  hcGetCrqsOnQueueWithError,
  hcPollCrqsToCreate,
  hcPollCrqsOnQueueWithError,
  hcCrqsToCreateAction,
  hcCrqsOnQueueWithErrorAction,
} from '../Actions/HelixCrqsThunks';
import './HelixCreations.css';

const useStyles = makeStyles((theme) => ({
  root:      { padding: theme.spacing(3) },
  container: { marginTop: theme.spacing(3) },
}));

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = {
  X: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  ChevronDown: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  Clock: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatTimestamp = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

// ─── MESSAGE MODAL ────────────────────────────────────────────────────────────
function MessageModal({ message, onClose }) {
  return (
    <div className="hc-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="hc-modal">
        <div className="hc-modal-header">
          <p className="hc-modal-title">Message</p>
          <button className="hc-close-btn" onClick={onClose}><Icon.X /></button>
        </div>
        <div className="hc-modal-body">
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: "'Inter', sans-serif" }}>
            {message}
          </p>
        </div>
        <div className="hc-modal-footer">
          <button className="hc-btn hc-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── ACTIONS DROPDOWN ─────────────────────────────────────────────────────────
function ActionsDropdown({ selectedIds, onAction, disabled }) {
  const [open, setOpen]                 = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset]   = useState(false);
  const ref                             = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handle = (action) => {
    setOpen(false);
    if (action === 'Delete') { setConfirmDelete(true); return; }
    if (action === 'Reset')  { setConfirmReset(true);  return; }
    onAction(action);
  };

  return (
    <>
      {confirmReset && (
        <div className="hc-modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmReset(false)}>
          <div className="hc-modal" style={{ maxWidth: 400 }}>
            <div className="hc-modal-header">
              <p className="hc-modal-title">Confirm Reset</p>
              <button className="hc-close-btn" onClick={() => setConfirmReset(false)}><Icon.X /></button>
            </div>
            <div className="hc-modal-body">
              <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>
                Are you sure you want to reset <strong>{selectedIds.length}</strong> record{selectedIds.length !== 1 ? 's' : ''}?
              </p>
            </div>
            <div className="hc-modal-footer">
              <button className="hc-btn hc-btn-ghost" onClick={() => setConfirmReset(false)}>Cancel</button>
              <button
                className="hc-btn hc-btn-primary"
                onClick={() => { setConfirmReset(false); onAction('Reset'); }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="hc-modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmDelete(false)}>
          <div className="hc-modal" style={{ maxWidth: 400 }}>
            <div className="hc-modal-header">
              <p className="hc-modal-title">Confirm Delete</p>
              <button className="hc-close-btn" onClick={() => setConfirmDelete(false)}><Icon.X /></button>
            </div>
            <div className="hc-modal-body">
              <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>
                Are you sure you want to delete <strong>{selectedIds.length}</strong> record{selectedIds.length !== 1 ? 's' : ''}? This action cannot be undone.
              </p>
            </div>
            <div className="hc-modal-footer">
              <button className="hc-btn hc-btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button
                className="hc-btn hc-btn-danger"
                onClick={() => { setConfirmDelete(false); onAction('Delete'); }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="hc-dropdown" ref={ref}>
      <button
        className="hc-dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || selectedIds.length === 0}
      >
        Actions
        {selectedIds.length > 0 && <span className="hc-dropdown-count">{selectedIds.length}</span>}
        <Icon.ChevronDown />
      </button>
      {open && (
        <div className="hc-dropdown-menu">
          <button className="hc-dropdown-item" onClick={() => handle('Reset')}>
            <span className="hc-dropdown-item-icon hc-dropdown-item-icon--reset">↺</span>
            Reset
          </button>
          <div className="hc-dropdown-divider" />
          <button className="hc-dropdown-item hc-dropdown-item--danger" onClick={() => handle('Delete')}>
            <span className="hc-dropdown-item-icon">✕</span>
            Delete
          </button>
        </div>
      )}
    </div>
    </>
  );
}

// ─── CHANGE BOT DROPDOWN ──────────────────────────────────────────────────────
const BOT_OPTIONS = [1, 2, 3, 4, 5];

function ChangeBotDropdown({ selectedIds, onChangeBot, disabled }) {
  const [open, setOpen] = useState(false);
  const ref             = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handle = (bot) => { setOpen(false); onChangeBot(bot); };

  return (
    <div className="hc-dropdown" ref={ref}>
      <button
        className="hc-dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || selectedIds.length === 0}
      >
        Change Bot
        {selectedIds.length > 0 && <span className="hc-dropdown-count">{selectedIds.length}</span>}
        <Icon.ChevronDown />
      </button>
      {open && (
        <div className="hc-dropdown-menu">
          {BOT_OPTIONS.map((bot) => (
            <button key={bot} className="hc-dropdown-item" onClick={() => handle(bot)}>
              <span className="hc-dropdown-item-icon" style={{ color: '#7c3aed' }}>⬡</span>
              Bot {bot}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── HISTORY TOGGLE ───────────────────────────────────────────────────────────
function HistoryToggle({ value, onChange }) {
  return (
    <button
      className={`hc-history-toggle${value ? ' hc-history-toggle--active' : ''}`}
      onClick={() => onChange(!value)}
      title={value ? 'Showing history — click to hide' : 'Show history'}
    >
      <Icon.Clock size={13} color={value ? '#7c3aed' : '#94a3b8'} />
      History
    </button>
  );
}

// ─── CRQ TABLE ────────────────────────────────────────────────────────────────
function CrqTable({ rows, loading, onMessageClick, selected, onToggle, onToggleAll }) {
  const allSelected  = rows.length > 0 && rows.every((r) => selected.some((x) => x.id === r.id));
  const someSelected = !allSelected && rows.some((r) => selected.some((x) => x.id === r.id));
  const colSpan      = 15;

  return (
    <div className="hc-table-wrapper">
      <table className="hc-table">
        <thead>
          <tr>
            <th style={{ width: 36, textAlign: 'center' }}>
              <input
                type="checkbox"
                className="hc-checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={() => onToggleAll(rows)}
                disabled={loading || rows.length === 0}
              />
            </th>
            <th>CRQ Change ID</th>
            <th>CI Number</th>
            <th>Scheduled Start</th>
            <th>Scheduled End</th>
            <th>Extension</th>
            <th>Supplier Company</th>
            <th>Warning</th>
            <th>Error</th>
            <th>Message</th>
            <th>RPA Start</th>
            <th>RPA End</th>
            <th>User</th>
            <th>Bot</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colSpan} className="hc-table-empty">
                <span className="hc-spinner" style={{ marginRight: 8 }} />
                Loading...
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="hc-table-empty">No data available</td>
            </tr>
          ) : rows.map((row) => {
            const isSelected = selected.some((x) => x.id === row.id);
            return (
              <tr key={row.id} className={isSelected ? 'hc-row-selected' : ''}>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    className="hc-checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(row)}
                  />
                </td>
                <td>{row.crqChangeId}</td>
                <td>{row.ciNumber}</td>
                <td>{formatTimestamp(row.scheduledStartDate)}</td>
                <td>{formatTimestamp(row.scheduledEndDate)}</td>
                <td>{row.extension}</td>
                <td>{row.supplierCompany}</td>
                <td>{row.warning ? <span className="hc-table-warn">⚠ {row.warning === true ? 'Warning' : row.warning}</span> : ''}</td>
                <td>{row.error === true ? <span className="hc-table-error">✕ Error</span> : (row.error ? <span className="hc-table-error">{row.error}</span> : '')}</td>
                <td
                  className={`hc-table-msg${row.message ? ' hc-table-msg-clickable' : ''}`}
                  onClick={() => row.message && onMessageClick(row.message)}
                  title={row.message}
                >
                  {row.message}
                </td>
                <td>{formatTimestamp(row.rpaStart)}</td>
                <td>{formatTimestamp(row.rpaEnd)}</td>
                <td>{row.user}</td>
                <td>{row.bot}</td>
                <td>
                  {row.status === 'ONGOING' && <span className="hc-status-spinning" title="Ongoing" />}
                  {row.status === 'DONE' && (
                    <svg className="hc-status-done" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                  {row.status === 'ERROR' && (
                    <svg className="hc-status-error" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function HelixCreations() {
  const classes              = useStyles();
  const dispatch             = useDispatch();
  const loading              = useSelector((state) => state.helixCrqsReducer.loading);
  const crqsToCreate         = useSelector((state) => state.helixCrqsReducer.crqsToCreate);
  const crqsOnQueueWithError = useSelector((state) => state.helixCrqsReducer.crqsOnQueueWithError);
  const userId               = useSelector((state) => state.session.user?.id);

  const [selectedMessage,          setSelectedMessage]          = useState(null);
  const [selectedToCreate,         setSelectedToCreate]         = useState([]);
  const [selectedOnQueueWithError, setSelectedOnQueueWithError] = useState([]);
  const [historyToCreate,          setHistoryToCreate]          = useState(false);
  const [historyOnQueueWithError,  setHistoryOnQueueWithError]  = useState(false);
  const pollingRef = useRef(null);

  const stopPolling = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  };

  // keep refs so the interval closure always reads the latest value
  const historyToCreateRef         = useRef(historyToCreate);
  const historyOnQueueWithErrorRef = useRef(historyOnQueueWithError);
  useEffect(() => { historyToCreateRef.current = historyToCreate; }, [historyToCreate]);
  useEffect(() => { historyOnQueueWithErrorRef.current = historyOnQueueWithError; }, [historyOnQueueWithError]);

  useEffect(() => {
    dispatch(hcGetCrqsToCreate(historyToCreate));
    dispatch(hcGetCrqsOnQueueWithError(historyOnQueueWithError));
    pollingRef.current = setInterval(() => {
      dispatch(hcPollCrqsToCreate(historyToCreateRef.current));
      dispatch(hcPollCrqsOnQueueWithError(historyOnQueueWithErrorRef.current));
    }, 5000);
    return () => stopPolling();
  }, []);

  // re-fetch immediately when history toggles
  useEffect(() => {
    stopPolling();
    dispatch(hcGetCrqsToCreate(historyToCreate));
    pollingRef.current = setInterval(() => {
      dispatch(hcPollCrqsToCreate(historyToCreateRef.current));
      dispatch(hcPollCrqsOnQueueWithError(historyOnQueueWithErrorRef.current));
    }, 5000);
  }, [historyToCreate]);

  useEffect(() => {
    stopPolling();
    dispatch(hcGetCrqsOnQueueWithError(historyOnQueueWithError));
    pollingRef.current = setInterval(() => {
      dispatch(hcPollCrqsToCreate(historyToCreateRef.current));
      dispatch(hcPollCrqsOnQueueWithError(historyOnQueueWithErrorRef.current));
    }, 5000);
  }, [historyOnQueueWithError]);

  // ── selection ─────────────────────────────────────────────────────────────
  const handleToggle = useCallback((setter, row) => {
    setter((prev) =>
      prev.some((x) => x.id === row.id)
        ? prev.filter((x) => x.id !== row.id)
        : [...prev, { id: row.id, crqChangeId: row.crqChangeId }]
    );
  }, []);

  const handleToggleAll = useCallback((setter, rows, selected) => {
    const allSelected = rows.every((r) => selected.some((x) => x.id === r.id));
    setter(allSelected ? [] : rows.map((r) => ({ id: r.id, crqChangeId: r.crqChangeId })));
  }, []);

  // ── actions ───────────────────────────────────────────────────────────────
  const handleCrqsToCreateAction = useCallback((action, selected, setter, bot = 0) => {
    const idList       = selected.map((x) => x.id);
    const crqChangeIds = selected.map((x) => x.crqChangeId);
    dispatch(hcCrqsToCreateAction(userId, action, idList, crqChangeIds, bot)).then(() => {
      setter([]);
      dispatch(hcGetCrqsToCreate(historyToCreate));
      dispatch(hcGetCrqsOnQueueWithError(historyOnQueueWithError));
    });
  }, [dispatch, userId, historyToCreate, historyOnQueueWithError]);

  const handleCrqsOnQueueWithErrorAction = useCallback((action, selected, setter, bot = 0) => {
    const idList       = selected.map((x) => x.id);
    const crqChangeIds = selected.map((x) => x.crqChangeId);
    dispatch(hcCrqsOnQueueWithErrorAction(userId, action, idList, crqChangeIds, bot)).then(() => {
      setter([]);
      dispatch(hcGetCrqsToCreate(historyToCreate));
      dispatch(hcGetCrqsOnQueueWithError(historyOnQueueWithError));
    });
  }, [dispatch, userId, historyToCreate, historyOnQueueWithError]);

  return (
    <Page className={classes.root} title="Helix Creations">
      <Header module="NASP" view="Helix Creations" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="hc-root">

            {selectedMessage && (
              <MessageModal message={selectedMessage} onClose={() => setSelectedMessage(null)} />
            )}

            <div style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── CRQ(s) to Create ───────────────────────────────────────── */}
              <div className="hc-card">
                <div className="hc-card-header">
                  <span className="hc-card-title">CRQ(s) to Create</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <HistoryToggle value={historyToCreate} onChange={setHistoryToCreate} />
                    <ActionsDropdown
                      selectedIds={selectedToCreate}
                      onAction={(action) => handleCrqsToCreateAction(action, selectedToCreate, setSelectedToCreate)}
                      disabled={loading.crqsToCreateAction || historyToCreate}
                    />
                    <ChangeBotDropdown
                      selectedIds={selectedToCreate}
                      onChangeBot={(bot) => handleCrqsToCreateAction('ChangeBot', selectedToCreate, setSelectedToCreate, bot)}
                      disabled={loading.crqsToCreateAction || historyToCreate}
                    />                    <span className="hc-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>Pending</span>
                  </div>
                </div>
                <div className="hc-card-body" style={{ padding: 0 }}>
                  <CrqTable
                    rows={crqsToCreate}
                    loading={loading.crqsToCreate}
                    onMessageClick={setSelectedMessage}
                    selected={selectedToCreate}
                    onToggle={(row) => handleToggle(setSelectedToCreate, row)}
                    onToggleAll={(rows) => handleToggleAll(setSelectedToCreate, rows, selectedToCreate)}
                  />
                </div>
              </div>

              {/* ── CRQ(s) on Queue with Error ─────────────────────────────── */}
              <div className="hc-card">
                <div className="hc-card-header">
                  <span className="hc-card-title">CRQ(s) on Queue with Error</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <HistoryToggle value={historyOnQueueWithError} onChange={setHistoryOnQueueWithError} />
                    <ActionsDropdown
                      selectedIds={selectedOnQueueWithError}
                      onAction={(action) => handleCrqsOnQueueWithErrorAction(action, selectedOnQueueWithError, setSelectedOnQueueWithError)}
                      disabled={loading.crqsOnQueueWithErrorAction || historyOnQueueWithError}
                    />
                    <ChangeBotDropdown
                      selectedIds={selectedOnQueueWithError}
                      onChangeBot={(bot) => handleCrqsOnQueueWithErrorAction('ChangeBot', selectedOnQueueWithError, setSelectedOnQueueWithError, bot)}
                      disabled={loading.crqsOnQueueWithErrorAction || historyOnQueueWithError}
                    />                    <span className="hc-badge" style={{ background: '#fffbeb', color: '#d97706' }}>Queue</span>
                  </div>
                </div>
                <div className="hc-card-body" style={{ padding: 0 }}>
                  <CrqTable
                    rows={crqsOnQueueWithError}
                    loading={loading.crqsOnQueueWithError}
                    onMessageClick={setSelectedMessage}
                    selected={selectedOnQueueWithError}
                    onToggle={(row) => handleToggle(setSelectedOnQueueWithError, row)}
                    onToggleAll={(rows) => handleToggleAll(setSelectedOnQueueWithError, rows, selectedOnQueueWithError)}
                  />
                </div>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default HelixCreations;